import {Injectable, OnDestroy} from '@angular/core';

import {Observable, Observer, Subscription, fromEvent, merge, timer} from 'rxjs';
import {exhaustMap, filter, take, tap} from 'rxjs/operators';

import {getRefreshUrl} from 'frontend/utils/auth_helpers';

const IFRAME_ID = 'refresh-iframe';

// Adjusted to 25 mins for a 45-60 min token
const DEFAULT_REFRESH_INTERVAL_MS = 25 * 60 * 1000;

// Don't refresh more than once every 5 minutes, even on tab switch
const MIN_REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

const REDIRECT_WAIT_MS = 5000;

/**
 * Service for proactively refreshing the auth session token via a hidden background iframe.
 */
@Injectable({providedIn: 'root'})
export class RefreshTokenService implements OnDestroy {
  private refreshSubscription?: Subscription;
  private lastRefreshTimestamp = 0;

  ngOnDestroy() {
    this.stopTokenRefresh();
    this.removeIframe();
  }

  /** Starts polling to refresh the session token at a regular interval. */
  startTokenRefresh() {
    if (typeof document === 'undefined') {
      return;
    }

    this.stopTokenRefresh();

    // Timer-based interval
    const interval = timer(DEFAULT_REFRESH_INTERVAL_MS, DEFAULT_REFRESH_INTERVAL_MS);

    // Tab-focus detection
    const visibilityChange = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
    );

    this.refreshSubscription = merge(interval, visibilityChange)
      .pipe(
        // Only proceed if we haven't refreshed very recently
        filter(() => this.shouldRefresh()),
        // exhaustMap ignores new signals while the current iframe is loading
        exhaustMap(() =>
          this.loadRenewalIframe().pipe(tap(() => (this.lastRefreshTimestamp = Date.now()))),
        ),
      )
      .subscribe();
  }

  private shouldRefresh(): boolean {
    const now = Date.now();
    return now - this.lastRefreshTimestamp > MIN_REFRESH_COOLDOWN_MS;
  }

  /** Creates a hidden iframe to execute the token renewal flow. */
  private loadRenewalIframe(): Observable<void> {
    return new Observable<void>((observer: Observer<void>) => {
      if (typeof document === 'undefined') {
        observer.next();
        observer.complete();
        return;
      }

      const refreshUrl = getRefreshUrl();
      if (!refreshUrl) {
        observer.next();
        observer.complete();
        return;
      }

      this.removeIframe();

      const iframe = document.createElement('iframe');
      iframe.setAttribute('id', IFRAME_ID);
      iframe.setAttribute('title', 'Session Refresher');
      // Explicitly tells screen readers to ignore the element entirely
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'width: 0; height: 0; border: none; display: none;';
      iframe.src = refreshUrl;
      document.body.appendChild(iframe);

      // Fixed timer to wait for the renewal to finish.
      const timerId = setTimeout(() => {
        observer.next();
        observer.complete();
      }, REDIRECT_WAIT_MS);

      return () => {
        clearTimeout(timerId);
        this.removeIframe();
      };
    }).pipe(take(1));
  }

  /** Stops the token refresh polling. */
  private stopTokenRefresh() {
    this.refreshSubscription?.unsubscribe();
  }

  private removeIframe() {
    if (typeof document === 'undefined') {
      return;
    }
    document.getElementById(IFRAME_ID)?.remove();
  }
}
